/**
 * PreferenceService - Handles API communication for user preferences
 * Manages page size preferences for the Funding Request table
 */

import SessionManager from '../utils/auth/SessionManager';

// API endpoint - loaded from runtime config.js
const API_BASE_URL = window.APP_CONFIG.API_BASE_URL;

// Valid page size options
const VALID_PAGE_SIZES = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

/**
 * Retry logic for transient failures
 * @param {Function} fn - The async function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} delayMs - Delay between retries in milliseconds
 * @returns {Promise} The result of the function
 */
const retryWithBackoff = async (fn, maxRetries = 2, delayMs = 500) => {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      // Only retry on network errors or 5xx errors
      const isRetryable = error.message.includes('Failed to fetch') || 
                         error.message.includes('500') || 
                         error.message.includes('503') ||
                         error.message.includes('timeout');
      
      if (!isRetryable || attempt === maxRetries) {
        throw error;
      }
      
      // Exponential backoff
      const delay = delayMs * Math.pow(2, attempt);
      console.log(`Retrying after ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw lastError;
};

/**
 * Get user's saved page size preference
 * @param {string} userId - The user's ID from Cognito
 * @returns {Promise<{pageSize: number, lastUpdated: string|null}>} User's preference or default
 * @throws {Error} If API call fails
 */
export const getPageSize = async (userId) => {
  if (!userId) {
    console.warn('getPageSize called without userId, returning default');
    return {
      pageSize: DEFAULT_PAGE_SIZE,
      lastUpdated: null
    };
  }

  return retryWithBackoff(async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/user-preferences/${userId}/page-size`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': getAuthToken()
          }
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return {
        pageSize: data.pageSize || DEFAULT_PAGE_SIZE,
        lastUpdated: data.lastUpdated || null
      };
    } catch (error) {
      console.error('Failed to get page size preference:', error);
      throw error;
    }
  });
};

/**
 * Save user's page size preference
 * @param {string} userId - The user's ID from Cognito
 * @param {number} pageSize - The page size to save (must be in [10, 25, 50, 100])
 * @returns {Promise<{success: boolean, pageSize: number, lastUpdated: string}>} Save result
 * @throws {Error} If validation fails or API call fails
 */
export const savePageSize = async (userId, pageSize) => {
  if (!userId) {
    throw new Error('userId is required');
  }

  // Validate page size
  if (!Number.isInteger(pageSize) || !VALID_PAGE_SIZES.includes(pageSize)) {
    throw new Error(
      `Invalid pageSize: ${pageSize}. Must be one of: ${VALID_PAGE_SIZES.join(', ')}`
    );
  }

  return retryWithBackoff(async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/user-preferences/${userId}/page-size`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': getAuthToken()
          },
          body: JSON.stringify({ pageSize })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `API error: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      return {
        success: data.success || true,
        pageSize: data.pageSize,
        lastUpdated: data.lastUpdated
      };
    } catch (error) {
      console.error('Failed to save page size preference:', error);
      throw error;
    }
  });
};

/**
 * Get the authentication token from SessionManager
 * @returns {string} The ID token for API authorization
 */
const getAuthToken = () => {
  return SessionManager.getIdToken();
};

/**
 * Set the authentication token (called by auth system)
 * @param {string} token - The ID token to store
 */
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('idToken', token);
  }
};

/**
 * Clear the authentication token (called on logout)
 */
export const clearAuthToken = () => {
  localStorage.removeItem('idToken');
  sessionStorage.removeItem('idToken');
};

export default {
  getPageSize,
  savePageSize,
  setAuthToken,
  clearAuthToken,
  VALID_PAGE_SIZES,
  DEFAULT_PAGE_SIZE
};
